import { BCMSRecord, BCMSStateManagerCRUD, Branding, useBCMSContext, useBCMSModal, useModel, useParams, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import MakeTransfer from "components/workflows/MakeTransferWorkflow/classes/MakeTransfer";
import MakeTransferUI from "components/workflows/MakeTransferWorkflow/classes/MakeTransferUI";
import Classes, { Account, TransferTemplate, TransferTransaction } from "model";
import React, { useEffect } from "react";

import CustomSelect, { CustomAccountSelect, CustomSelectItem } from 'components/custom-ui/CustomSelect';
import CustomAccountWithAddButtonSelect from "components/custom-ui/CustomSelect/components/CustomAccountWithAddButtonSelect";
import DateSelect from 'components/custom-ui/DateSelect';
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRow, FormRowView, PageContainer, PageContentView } from 'components/shared';
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { CheckmarkBanner } from "components/shared/views/ConfirmBannerView";
import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";
import { UIAmountInput, UIButton, UICheckbox, UITextInput } from 'components/ui';
import { UITextClearInput } from "components/ui/UITextInput";
import { SVGAsset } from 'res';
import './mobile.css';
import './styles.css';

/**
 * @name TransfersPage
 * @param {OBJECT} data - Data passed to the page
 * @param {OBJECT} ui - UI object to set values of the pages ui
 * @returns {React.Component}
 * @description The main component for the TransfersPage
 */
export default function TransfersPage(props) {
    // Initalize all model classes
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);
    const { branding, user } = useModel();
    const { transferID } = useParams();
    const navigate = useRoute();

    // Sets the values for the data and ui variables
    const data = { ...getDefaultPageData(transferID), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(data.transfer, context), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";

    const { nextStep } = useWorkflow() ?? {};
    // The state to be passed to the state manager
    const state = { data, ui };

    // :: React.state, React.setState -> OBJECT
    // Function to initalize the controller for the state manager
    const controller = (state, setState) => ({
        data: state.data, // Set the data property in the controller
        ui: state.ui, // Set the ui property in the controller

        // onChange :: STRING, FUNCTION|UNDEFINED -> * -> VOID
        // Method to update the transfer within the data based on the passed property
        // and value passed.
        onChange: (prop, callback) => value => {
            // Check if the callback property is a function, if so call the callback
            // passing the value and prop
            const additionalUpdates = typeof callback === "function"
              ? callback(value, prop)
              : undefined;
              
            // If the returned value of the callback was an instance of BCMSStateManagerCRUD
            // then set that value otherwise initalize a new instance of BCMSStateManagerCRUD
            const needToCombine = additionalUpdates instanceof BCMSStateManagerCRUD
              ? additionalUpdates
              : BCMSStateManagerCRUD.init({});
            // Combine the additional updates returned from the callback and update the transfer
            // based on the prop and value passed
            const transferUpdates = BCMSStateManagerCRUD.init(state)
                .combine(needToCombine)
                .update([ "data", "transfer", prop ], value)
                .submit();

            // Using the updated data, update the control property of the ui to include the new values
            // set in the transfer. On submit update state
            BCMSStateManagerCRUD.init(transferUpdates) 
              .update([ "ui", "control" ], MakeTransferUI.init(transferUpdates.data.transfer, branding))
              .submit(setState);
        },

        // changeEstimatedDate :: *, STRING -> BCMSStateManagerCRUD
        // Return an instance of BCMSStateManagerCRUD with the updates to the transfer
        // based on the passed prop and value parameters
        changeEstimatedDate: (value, prop) => {
            return BCMSStateManagerCRUD.init(state)
                .update([ "data", "transfer", prop ], value)
                .update([ "data", "transfer", "estimatedDeliveryDate" ], value);
        },

                // changeFrom :: *, STRING -> BCMSStateManagerCRUD|undefined
        // If the id property of the value is equal to the id of the to account
        // then return an instance of BCMSStateManager to update the transfer.
        // Otherwise return an undefined
        changeTemplate: (value, prop) => {
            // Prevent rerender if a dropdown is opened but nothing is changed.
            if(value.id === state.data.transfer.template.id) return;

            return BCMSStateManagerCRUD.init(state)
            .update([ "data", "transfer", prop ], value)
            .update([ "data", "transfer", "amount" ], value.id === "prompt" ? "" : value.defaultAmount.amount)
            .update([ "data", "transfer", "from" ], value.id === "prompt" ? value.fromAccount : Account.initByType(value.fromAccount.id))
            .update([ "data", "transfer", "to" ], value.id === "prompt" ? value.toAccount : Account.initByType(value.toAccount.id))
            .update([ "data", "transfer", "fromTemplateAccount" ], value.id === "prompt" ? value.fromAccount : Account.initByType(value.fromAccount.id))
            .update([ "data", "transfer", "toTemplateAccount" ], value.id === "prompt" ? value.toAccount : Account.initByType(value.toAccount.id))
        },

        // changeFrom :: *, STRING -> BCMSStateManagerCRUD|undefined
        // If the id property of the value is equal to the id of the to account
        // then return an instance of BCMSStateManager to update the transfer.
        // Otherwise return an undefined
        changeFrom: (value, prop) => {
            if(value.id === state.data.transfer.to.id) {
                return BCMSStateManagerCRUD.init(state)
                    .update([ "data", "transfer", prop ], value)
                    .update([ "data", "transfer", "to" ], "");
            }

            return undefined;
        },

        // changeSchedule :: * -> BCMSStateManagerCRUD|undefined
        // If the passed value is equal to "Future (One Time)" then return an instance
        // of BCMSStateManagerCRUD to update the sendOn property of the transfer. Otherwise
        // return an undefined
        changeSchedule: value => {
            if(value !== "Future (One Time)") {
                return BCMSStateManagerCRUD.init(state)
                    .update([ "data", "transfer", "sendOn" ], "");
            }

            return undefined;
        },

        onAddAccountModalButtonClick: (addedUnlinkedAcc) => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "addedUnlinkedAccount" ], addedUnlinkedAcc)
            .update([ "data", "allToAccounts"], Account.initAllIncludingUnlinked())
            .update([ "ui", "successfullyAddedUnlinkedAccount", "isActive"], true)
            .submit(setState);
        },

        onAddAccountPageButtonClick: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", "successfullyAddedUnlinkedAccount", "isActive"], false)
            .submit(setState);
        },

        onAddUnlinkedTransferAccountModalClose: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", "successfullyAddedUnlinkedAccount", "isActive"], false)
            .submit(setState);
        },

        onCancelButtonClick: () => navigate("/transferActivity/scheduledTransfers"),

        // submit :: Event -> VOID
        // Call the nextStep function passing an object containing the data with the updated transfer object
        // to the next step in the workflow
        submit: evt => nextStep({ data: { ...state.data, transfer: state.data.transfer.setDefaults(branding) }})
    });

    return (
        <PageContainer className={"transfers-page"+className} state={ state } controller={ controller }>
            <TransferContent />
        </PageContainer>
    );
}

/**
 * @name getDefaultPageData
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = (transferID) => {
    const { branding, user } = GetModel();

    // Note: user settings determine how transfers are made, 
    // so using templates, using only templates, or no templates at all.
    const allowTransferOnlyTemplates = user.allowTransferOnlyTemplates === "true";
    let editTransfer = undefined;
    let editMakeTransfer = undefined;

    if(typeof transferID === "string") {
        editTransfer = TransferTransaction.initByID(transferID);
        editMakeTransfer = MakeTransfer.init(editTransfer, branding)
        editMakeTransfer.templateOnly = allowTransferOnlyTemplates;
    }

    const transfer = editMakeTransfer ? editMakeTransfer : MakeTransfer.init({ templateOnly: allowTransferOnlyTemplates }, branding);

    return {
        transfer: transfer,
        transferTemplates: TransferTemplate.initAll(),
        editingTransfer: { isEditing: transferID ? "true" : "false" },
        editingTransferID: { id: transferID },
        addedUnlinkedAccount: undefined,
        allToAccounts: Account.initAllIncludingUnlinked()
    }
};

function GetModel() {
    return useModel();
}

/**
 * @name getDefaultPageUI
 * @param {MakeTransfer} transfer - A MakeTransfer instance used to initalize an instance of MakeTransferUI
 * @returns {OBJECT}
 * @description Hook to return the default ui required by the page
 * @note The transfer parameter is not required, if nothing is passed than an empty instance of MakeTransfer will be used
 */
export const getDefaultPageUI = (transfer, context) => {
    BCMSRecord.globalControllerFor(Classes, context);
    const { branding } = GetModel();

    return {
        control: MakeTransferUI.init(transfer ?? MakeTransfer.init(), branding),
        template: { isActive: false },
        from: { isActive: false },
        to: { isActive: false },
        paymentOption: { isActive: false },
        schedule: { isActive: false },
        frequency: { isActive: false },
        firstTransferDay: { isActive: false },
        secondTransferDay: { isActive: false },
        duration: { isActive: false },
        startDate: { isActive: false },
        successfullyAddedUnlinkedAccount: { isActive: false },
        addUnlinkedTransferAccountModal: { isActive: false }
    }
};

/**
 * @name TransferContent
 * @returns {React.Component}
 * @description The component that contains all the content shown for the TransfersPage
 */
function TransferContent(props) {    
    const context = useStateManager();
    const { open } = useBCMSModal();
    const { branding } = useModel();
    const { options } = branding;
    // Grab the data and ui properties from the state manager context
    const { data, ui, onCancelButtonClick } = context;
    const accounts = Account.initAll().filter(account => account.type === "checking" || account.type === "savings", []);
    const templates = data.transferTemplates;

    // Get the functions used by the ui from the context
    const { onChange, changeEstimatedDate, changeTemplate, changeFrom, changeSchedule, submit, onAddUnlinkedTransferAccountModalClose } = context;

    const transfer = data.transfer;
    const template = transfer.template ?? {};
    const hasSelectedTemplate = template.id !== "prompt";
    const control = ui.control;
    const subHeaderContent = options.transfers_subheader_text_abi22; 
    const promoText = options.transfers_promo_abi22; 
    const subfooterText = options.transfers_subfooter_abi22;
    let amount = transfer.amount ? transfer.amount.amount : "";
    const selectedTemplate = data.transfer.template;

    const transferTemplateOptions = templates.reduce((acc, item, i) => {
        if(i === 0) {
            // If "templateOnly" is true, do not show the "Open Transfer" option.
            if(!transfer.templateOnly) {
                const templatePromptItem = TransferTemplate.init({ id: "prompt", name: "Open Transfer" });
                acc.push(
                    <CustomSelectItem key={ templatePromptItem.id } value={ templatePromptItem } dataPoint={ `Template_Select_Option_Prompt` }>{ templatePromptItem.name }</CustomSelectItem>
                );
            }
            
        }
        acc.push(
            <CustomSelectItem key={ item.id } value={ item } dataPoint={ `Template_Select_Option_${ item.id }` }>{ item.name }</CustomSelectItem>
        )
        return acc;
    }, []);

    useEffect(() => {
        if(ui.addUnlinkedTransferAccountModal.isActive) {
            open("AddUnlinkedTransferAccountModal");
        }
    } , [ui]);

    return (
        <PageContentView>
            <div className="flex-container flex-vertical page-widget-wrapper">
                {promoText && <CustomContentHeaderWidget content = {promoText}/>}
                { ui.successfullyAddedUnlinkedAccount.isActive 
                && <CheckmarkBanner text={ `"${ data.addedUnlinkedAccount.nickname }" has been added as an unlinked account. ` } 
                dataPoint="" onClick={ onAddUnlinkedTransferAccountModalClose } /> }

                <CenterContentContainer>
                    <ContentHeaderView text={ control.isEditingTransfer ? `Edit Transfer` : `Make a Transfer` } />
                    {subHeaderContent && <CustomContentHeaderWidget content = {subHeaderContent}/>}
                    <FormContainer className="small-form-width">
                        {   control.template && !control.isEditingTransfer &&
                            <FormRowView text="Template" data-point="Transfers_Template">
                                <CustomSelect value={ selectedTemplate } active={ ui.template.isActive } dataPoint={ `Template_Select` } onChange={ onChange("template", changeTemplate) }>
                                    { transferTemplateOptions }
                                </CustomSelect>
                            </FormRowView> 
                        }
                        { control.templateOnly && hasSelectedTemplate && <FormFields/> }
                        { !control.templateOnly && <FormFields/> }
                        
                        <FormButtonRow>
                            { control.isEditingTransfer && 
                                <UIButton onClick={ onCancelButtonClick } className="secondary-button" text="Cancel" />
                            }
                            <UIButton className="primary-button" text="Continue" onClick={ submit } data-point="Transfers_Continue"/>
                        </FormButtonRow>
                    </FormContainer>
                </CenterContentContainer> 
                { subfooterText && <CustomContentHeaderWidget content={ subfooterText } /> }
            </div>
        </PageContentView>
    );
}

/**
 * @name FormFields
 * @returns {React.Component}
 * @description These are the form fields in addition to the "Template" field, 
 * since these fields can be shown hidden depending on the value of the "Template" field.
 */
function FormFields(props) {
    const context = useStateManager();
    const { open } = useBCMSModal();
    const { branding } = useModel();
    const { options } = branding;

    const immediateOneTimeLabel = options.transfer_schedule_immediate_abi22;
    const showInterestOnly = options.interest_only_payment_abi22 === "true" ? true : false;

    // Grab the data and ui properties from the state manager context
    const { data, ui } = context;
    //const accounts = Account.initAll().filter(account => account.type === "checking" || account.type === "savings", []);
    const accounts = Account.initAllAsset();

    // Get the functions used by the ui from the context
    const { onChange, changeEstimatedDate, changeTemplate, changeFrom, changeSchedule, submit, onAddAccountClick } = context;
    const transfer = data.transfer;
    const template = transfer.template ?? {};
    const control = ui.control;
    let amount = transfer.amount ? transfer.amount.amount : "";

    const getAccountInfo = templateAccount => {
        const account = Account.initByType(templateAccount.id);
        let info = "";
        if(account.type === "loan") {
            info = `${ account.nickname } - ${ account.number } (Current ${ account.currentBalance.format() })`;
        } else if(account.type === "loc") {
            info = `${ account.nickname } - ${ account.number } (Available ${ account.availableCredit.format() })`;
        } else if(account.type === "unlinked") {
            info = `${ account.nickname } - ${ account.number } (Unlinked Account)`;
        } else {
            info = `${ account.nickname } - ${ account.number } (Available ${ account.availableBalance.format() })`;
        }
        return info;
    }

    const _onChange = (prop, callback) => value => {
        if(value === "AddAccount" && (prop === "from" || prop === "to")) {
            context.onAddAccountPageButtonClick();
            open("AddUnlinkedTransferAccountModal");
        } else {
            onChange(prop, callback)(value);
        }
    };

    const hasFromAddAccountButton = branding.options.addacct_fromdd_abi22 === "true";
    const hasToAddAccountButton = branding.options.addacct_todd_abi22 === "true";
    const showPrincipalOnly = branding.options.principal_only_payment_abi22 === "true";
 
    const showRegularPayment = branding.options.regular_payment_abi22 === "true";
    const showEscrowOnly = branding.options.escrow_only_abi22 === "true";
    const showEntireBalance = branding.options.entire_balance_payment_abi22 === "true";
    const showOtherAmount = branding.options.other_amount_abi22 === "true";
    
     
    return <>
    {   control.fromTemplateAccount && !control.isEditingTransfer &&
                            <FormRowView text="From" data-point="Transfers_From">
                                <div><b>{ getAccountInfo(template.fromAccount) } </b></div>
                            </FormRowView> }

                        {   control.toTemplateAccount && !control.isEditingTransfer && 
                            <FormRowView text="To" data-point="Transfers_To">
                                <div><b>{ getAccountInfo(template.toAccount) }</b></div>
                            </FormRowView> }

                        {   control.editFromAccount && 
                        <FormRowView text="From" data-point="Transfers_From">
                            <div><b>{ getAccountInfo(transfer.from) }</b></div>
                        </FormRowView> }

                        {   control.editToAccount && 
                        <FormRowView text="To" data-point="Transfers_To">
                            <div><b>{ getAccountInfo(transfer.to) }</b></div>
                        </FormRowView> }

                        {   control.from && !control.isEditingTransfer && 
                            <FormRowView text="From" data-point="Transfers_From">
                                { hasFromAddAccountButton 
                                && <CustomAccountWithAddButtonSelect accounts={ accounts } onChange={ _onChange("from", changeFrom) } value={ transfer.from ? Account.initByType(transfer.from.id) : transfer.from } active={ ui.from.isActive } />
                                }
                                { !hasFromAddAccountButton 
                                && <CustomAccountSelect accounts={ accounts } onChange={ _onChange("from", changeFrom) } value={ transfer.from ? Account.initByType(transfer.from.id) : transfer.from } active={ ui.from.isActive } />
                                }
                            </FormRowView> }

                        {   control.to && !control.isEditingTransfer && 
                            <FormRowView text="To" data-point="Transfers_To">
                                { hasToAddAccountButton 
                                && <CustomAccountWithAddButtonSelect accounts={ data.allToAccounts } onChange={ _onChange("to") } disabled={ transfer.from } value={ transfer.to ? Account.initByType(transfer.to.id) : transfer.to } active={ ui.to.isActive } />
                                }
                                { !hasToAddAccountButton 
                                && <CustomAccountSelect accounts={ data.allToAccounts } onChange={ _onChange("to") } disabled={ transfer.from } value={ transfer.to ? Account.initByType(transfer.to.id) : transfer.to } active={ ui.to.isActive } />
                                }
                            </FormRowView> }

                        {   control.paymentOption && 
                            <FormRowView text="Payment Option" data-point="Transfers_Payment_Option">
                                <Branding id="interest_only_payment_abi22" value="true" default="true">
                                    <CustomSelect value={ transfer.paymentOption } onChange={ onChange("paymentOption") } active={ ui.paymentOption.isActive }>
                                        <CustomSelectItem value="Payment Due" data-point="Payment_Option_Due">Payment Due { transfer.to ? Account.initByType(transfer.to.id).due.format() : undefined }</CustomSelectItem>

                                        { showRegularPayment && <CustomSelectItem value="Regular Payment" data-point="Payment_Option_Regular">Regular Payment</CustomSelectItem> }
                                        { showOtherAmount && <CustomSelectItem value="Other Amount" data-point="Payment_Option_Other">Other Amount</CustomSelectItem> }
                                        
                                        { showPrincipalOnly &&<CustomSelectItem value="Principal-Only Payment" data-point="Payment_Option_Principle">Principal-Only Payment</CustomSelectItem>}
                                        <CustomSelectItem value="Interest-Only Payment" data-point="Payment_Option_Interest">Interest-Only Payment</CustomSelectItem>
                                        { showEntireBalance && <CustomSelectItem value="Entire Balance">Entire Balance {transfer.to ?  Account.initByType(transfer.to.id).currentBalance.format():undefined }</CustomSelectItem> }
                                        { showEscrowOnly && <CustomSelectItem value="Escrow-Only Payment" data-point="Payment_Option_Escrow">Escrow-Only Payment</CustomSelectItem> }
                                    </CustomSelect>
                                </Branding>

                                <Branding id="interest_only_payment_abi22" value="false" default="true">
                                    <CustomSelect value={ transfer.paymentOption } onChange={ onChange("paymentOption") } active={ ui.paymentOption.isActive }>
                                        <CustomSelectItem value="Payment Due" data-point="Payment_Option_Due">Payment Due { transfer.to ? Account.initByType(transfer.to.id).due.format() : undefined }</CustomSelectItem>

                                        { showRegularPayment && <CustomSelectItem value="Regular Payment" data-point="Payment_Option_Regular">Regular Payment</CustomSelectItem> }
                                        { showOtherAmount && <CustomSelectItem value="Other Amount" data-point="Payment_Option_Other">Other Amount</CustomSelectItem> }

                                        { showPrincipalOnly &&  <CustomSelectItem value="Principal-Only Payment" data-point="Payment_Option_Principle">Principal-Only Payment</CustomSelectItem>}     
                                        { showEntireBalance && <CustomSelectItem value="Entire Balance">Entire Balance  {transfer.to ?  Account.initByType(transfer.to.id).currentBalance.format():undefined }</CustomSelectItem> }
                                        { showEscrowOnly && <CustomSelectItem value="Escrow-Only Payment" data-point="Payment_Option_Escrow">Escrow-Only Payment</CustomSelectItem> }
                                    </CustomSelect>
                                </Branding>
                            </FormRowView> }

                        {   control.amount && 
                            <FormRowView text="Amount" data-point="Transfers_Amount">
                                <UIAmountInput value={ amount } onChange={ onChange("amount") } data-point="Transfers_Amount_Field" />
                            </FormRowView> }

                        {   control.templateMaxAmount &&
                            <FormRowView text="Maximum Amount" data-point="Transfers_Amount">
                                <div><b>{ `${ template.maxAmount.format() } ` }</b></div>
                            </FormRowView> }

                        {   control.schedule && 
                            <FormRowView text="Schedule" data-point="Transfers_Schedule">
                                <CustomSelect value={ transfer.schedule } onChange={ onChange("schedule", changeSchedule) } active={ ui.schedule.isActive } data-point="Transfers_Schedule_Select">
                                    <CustomSelectItem data-point="Schedule_Immediate" value={ immediateOneTimeLabel }>{ immediateOneTimeLabel }</CustomSelectItem>
                                    <CustomSelectItem data-point="Schedule_Future" value="Future (One Time)">Future (One Time)</CustomSelectItem>
                                    <CustomSelectItem data-point="Schedule_Recurring" value="Recurring">Recurring</CustomSelectItem>
                                </CustomSelect>
                            </FormRowView> }

                        {   control.sendOn && 
                            <FormRowView text="Send On" data-point="Transfers_Send_On">
                                <DateSelect value={ transfer.sendOn } onChange={ onChange("sendOn", changeEstimatedDate) } />
                            </FormRowView> }

                        {   control.frequency && 
                            <FormRowView text="Frequency" data-point="Transfers_Frequency">
                                <CustomSelect value={ transfer.frequency } onChange={ onChange("frequency") } active={ ui.frequency.isActive } dataPoint="Frequency_Select">
                                    <CustomSelectItem data-point="Frequency_Daily">Daily</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Weekly">Weekly</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Two_Weeks">Every two weeks</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Twice_Month">Twice a month</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Four_Weeks">Every four weeks</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Monthly">Monthly</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Two_Months">Every two months</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Quarterly">Quarterly</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Six_Months">Every six months</CustomSelectItem>
                                    <CustomSelectItem data-point="Frequency_Annually">Annually</CustomSelectItem>
                                </CustomSelect>
                            </FormRowView> }

                        {   control.firstTransferDay &&
                            <FormRow 
                                data-point="Transfers_First_Transfer_Day" 
                                formLabel={
                                    <div><span>First Transfer Day</span><SVGAsset id="InfoIcon" className="inline-text-icon right-icon" /></div>
                                }>
                                <CustomSelect value={ transfer.firstTransferDay } onChange={ onChange("firstTransferDay") } active={ ui.firstTransferDay.isActive }>
                                    <CustomSelectItem data-point="First_Transfer_Day_1" value="1">1</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_2" value="2">2</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_3" value="3">3</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_4" value="4">4</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_5" value="5">5</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_6" value="6">6</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_7"  value="7">7</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_8"  value="8">8</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_9"  value="9">9</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_10"  value="10">10</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_11"  value="11">11</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_12"  value="12">12</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_13"  value="13">13</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_14"  value="14">14</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_15"  value="15">15</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_16"  value="16">16</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_17"  value="17">17</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_18"  value="18">18</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_19"  value="19">19</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_20"  value="20">20</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_21"  value="21">21</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_22"  value="22">22</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_23"  value="23">23</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_24"  value="24">24</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_25"  value="25">25</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_26"  value="26">26</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_27"  value="27">27</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_28"  value="28">28</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_29"  value="29">29</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_30"  value="30">30</CustomSelectItem>
                                    <CustomSelectItem data-point="First_Transfer_Day_31"  value="31">31</CustomSelectItem>
                                </CustomSelect>
                            </FormRow> }

                        {   control.secondTransferDay && 
                            <FormRow 
                                data-point="Transfers_Second_Transfer_Day" 
                                formLabel={
                                    <div>
                                        <span>Second Transfer Day</span>
                                        <SVGAsset id="InfoIcon" className="inline-text-icon right-icon" />
                                    </div>
                                }>
                                <CustomSelect value={ transfer.secondTransferDay } onChange={ onChange("secondTransferDay") } active={ ui.secondTransferDay.isActive }>             
                                    <CustomSelectItem data-point="Second_Transfer_Day_1" value="1">1</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_2" value="2">2</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_3" value="3">3</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_4" value="4">4</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_5" value="5">5</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_6" value="6">6</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_7"  value="7">7</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_8"  value="8">8</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_9"  value="9">9</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_10"  value="10">10</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_11"  value="11">11</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_12"  value="12">12</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_13"  value="13">13</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_14"  value="14">14</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_15"  value="15">15</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_16"  value="16">16</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_17"  value="17">17</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_18"  value="18">18</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_19"  value="19">19</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_20"  value="20">20</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_21"  value="21">21</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_22"  value="22">22</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_23"  value="23">23</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_24"  value="24">24</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_25"  value="25">25</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_26"  value="26">26</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_27"  value="27">27</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_28"  value="28">28</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_29"  value="29">29</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_30"  value="30">30</CustomSelectItem>
                                    <CustomSelectItem data-point="Second_Transfer_Day_31"  value="31">31</CustomSelectItem>
                                </CustomSelect>
                            </FormRow> }

                        {   control.startDate && 
                            <FormRowView text="Start Date" data-point="Transfers_Start_Date">
                                <DateSelect active={ ui.startDate.isActive } value={ transfer.startDate }
                                onChange={ onChange("startDate", changeEstimatedDate) } dataPoint="Start_Date_Select" />
                            </FormRowView> }

                        {   control.estimatedDeliveryDate && 
                            <FormRowView text="Estimated Delivery Date" data-point="Transfers_Estimated_Date">
                                <b>{ transfer.estimatedDeliveryDate.format ? transfer.estimatedDeliveryDate.format("m/d/Y") : "[MM/DD/YYYY]" }</b>
                            </FormRowView> }

                        {   control.duration && 
                            <FormRowView text="Duration" data-point="Transfers_Duration">
                                <CustomSelect value={ transfer.duration } onChange={ onChange("duration") } active={ ui.duration.isActive }>
                                    <CustomSelectItem data-point="Duration_No_End">No End Date</CustomSelectItem>
                                    <CustomSelectItem data-point="Duration_Date">End by a specified date</CustomSelectItem>
                                    <CustomSelectItem data-point="Duration_Number_Transfers">End after a specified number of transfers</CustomSelectItem>
                                </CustomSelect>
                            </FormRowView> }

                        {   control.endDate && 
                            <FormRowView text="End Date" data-point="Transfers_End_Date">
                                <DateSelect value={ transfer.endDate } onChange={ onChange("endDate") } />
                            </FormRowView> }

                        {   control.numberOfTransfers && 
                            <FormRowView text="Number of Transfers" data-point="Transfers_Number_Transfers">
                                <UITextInput value={ transfer.numberOfTransfers } onChange={ onChange("numberOfTransfers") } />
                            </FormRowView> }

                        {   control.description && 
                            <FormRowView text="Description (Optional)" data-point="Transfers_Description">
                                <UITextClearInput value={ transfer.description } onChange={ onChange("description") } dataPoint="Description_Input" />
                            </FormRowView> }

                        {   control.sendEmail &&
                            <Branding id="send_email_checkbox_abi22" value="true" default="true">
                                <FormRowView data-point="Transfers_Send_Email">
                                    <UICheckbox value={ transfer.sendEmail } onChange={ onChange("sendEmail") } label="Send me an email when this transfer is completed" dataPoint="Transfers_Checkbox_Container" />
                                </FormRowView>
                            </Branding> }
    </>
}